// ADR-0052 §2 / V101-2-3: event receiver verifiers. Each authenticates a
// provider's push and extracts only event METADATA (id, type, timestamp) —
// the payload is never evidence and is never persisted or logged.
//
// Web Crypto only; no runtime-specific imports.
import { hmacSha256Hex } from '../utils/audit-crypto.js';
export const EVENT_VERIFIERS = [
    'github_hmac',
    'okta_event_hook',
    'graph_change_notification',
    'aws_eventbridge',
    'gcp_pubsub_oidc',
    'azure_eventgrid',
];
/** Verifiers with an implementation; azure_eventgrid is specified only (v10.2). */
export const IMPLEMENTED_EVENT_VERIFIERS = new Set([
    'github_hmac', 'okta_event_hook', 'graph_change_notification', 'aws_eventbridge', 'gcp_pubsub_oidc',
]);
/** Accepted clock skew for provider timestamps (ADR-0052 §2). */
export const EVENT_MAX_SKEW_MS = 5 * 60_000;
const enc = new TextEncoder();
const reject = (reason) => ({ kind: 'reject', reason });
/** Constant-time comparison of two strings. */
export function timingSafeEqual(a, b) {
    const ab = enc.encode(a);
    const bb = enc.encode(b);
    let diff = ab.length ^ bb.length;
    const len = Math.max(ab.length, bb.length);
    for (let i = 0; i < len; i++)
        diff |= (ab[i] ?? 0) ^ (bb[i] ?? 0);
    return diff === 0;
}
function hex(buf) {
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
async function sha256Hex(s) {
    return hex(await crypto.subtle.digest('SHA-256', enc.encode(s)));
}
/**
 * ADR-0052 §2: per-connector webhook secret, derived — never stored.
 * Rotation = bump `event_secret_version`.
 */
export async function deriveEventSecret(masterKey, connectorId, version) {
    return hmacSha256Hex(masterKey, `connector-event:${connectorId}:${version}`);
}
function withinSkew(ts, now) {
    if (!ts)
        return true; // provider sends no timestamp: replay table alone protects
    const t = Date.parse(ts);
    return Number.isFinite(t) && Math.abs(now.getTime() - t) <= EVENT_MAX_SKEW_MS;
}
function parseJson(raw) {
    try {
        return JSON.parse(raw);
    }
    catch {
        return undefined;
    }
}
// ── github_hmac ─────────────────────────────────────────────────────────
async function verifyGithub(r) {
    if (r.method !== 'POST')
        return reject('method');
    const sig = r.header('x-hub-signature-256') ?? '';
    const delivery = r.header('x-github-delivery') ?? '';
    const event = r.header('x-github-event') ?? '';
    if (!sig.startsWith('sha256=') || !delivery || !event)
        return reject('missing headers');
    const expected = `sha256=${await hmacSha256Hex(r.secret, r.rawBody)}`;
    if (!timingSafeEqual(sig, expected))
        return reject('signature');
    // V104-C-1: X-GitHub-Delivery is outside the signature; dedupe on the signed body.
    return { kind: 'events', events: [{ providerEventId: `gh:${await sha256Hex(r.rawBody)}`, eventType: event }] };
}
// ── okta_event_hook ─────────────────────────────────────────────────────
async function verifyOkta(r) {
    const auth = r.header('authorization') ?? '';
    if (!timingSafeEqual(auth, r.secret))
        return reject('authorization');
    if (r.method === 'GET') {
        const challenge = r.header('x-okta-verification-challenge');
        if (!challenge)
            return reject('no challenge');
        return { kind: 'challenge', status: 200, body: JSON.stringify({ verification: challenge }), contentType: 'application/json' };
    }
    if (r.method !== 'POST')
        return reject('method');
    const body = parseJson(r.rawBody);
    const events = body?.data?.events;
    if (!Array.isArray(events) || events.length === 0)
        return reject('no events');
    const out = [];
    for (const e of events.slice(0, 100)) {
        if (typeof e.uuid !== 'string' || typeof e.eventType !== 'string')
            continue;
        if (!withinSkew(e.published, r.now))
            continue;
        out.push({ providerEventId: e.uuid, eventType: e.eventType, timestamp: e.published });
    }
    return out.length ? { kind: 'events', events: out } : reject('no valid events');
}
// ── graph_change_notification ───────────────────────────────────────────
/**
 * V104-C-2 (review L-2): connector config key holding the end of the pending-subscription
 * window (ISO 8601). The step that creates a Graph subscription sets it just before the
 * create call; outside the window a validation request is a plain 404.
 */
export const GRAPH_VALIDATION_UNTIL_KEY = 'graph_validation_until';
/** Longest window honoured, so a stale or far-future config value never keeps the echo open. */
export const GRAPH_VALIDATION_WINDOW_MS = 10 * 60_000;
/** Graph sends readable ASCII ("Validation: Testing client application reachability …"). */
const GRAPH_VALIDATION_TOKEN = /^[A-Za-z0-9 .:_+/=-]{1,1024}$/;
function graphValidationOpen(r) {
    const raw = r.config[GRAPH_VALIDATION_UNTIL_KEY];
    const until = typeof raw === 'string' ? Date.parse(raw) : NaN;
    const left = until - r.now.getTime();
    return Number.isFinite(left) && left > 0 && left <= GRAPH_VALIDATION_WINDOW_MS;
}
async function verifyGraph(r) {
    const validationToken = new URL(r.url).searchParams.get('validationToken');
    if (validationToken !== null) {
        // Subscription validation arrives before any clientState, so it is unauthenticated:
        // echo only while a subscription is pending, only plain characters, as text/plain.
        if (r.method !== 'POST' && r.method !== 'GET')
            return reject('method');
        if (!graphValidationOpen(r))
            return reject('no pending subscription');
        if (!GRAPH_VALIDATION_TOKEN.test(validationToken))
            return { kind: 'bad_request', reason: 'validation token charset' };
        return { kind: 'challenge', status: 200, body: validationToken, contentType: 'text/plain' };
    }
    if (r.method !== 'POST')
        return reject('method');
    const body = parseJson(r.rawBody);
    const items = body?.value;
    if (!Array.isArray(items) || items.length === 0)
        return reject('no notifications');
    const out = [];
    for (const n of items.slice(0, 100)) {
        if (typeof n.clientState !== 'string' || !timingSafeEqual(n.clientState, r.secret))
            return reject('clientState');
        const resource = typeof n.resource === 'string' ? n.resource : '';
        const changeType = typeof n.changeType === 'string' ? n.changeType : 'changed';
        // Graph notifications carry no id; hash the identifying fields (not the payload).
        const id = await sha256Hex([n.subscriptionId, resource, changeType, n.subscriptionExpirationDateTime, n.tenantId].join('|'));
        out.push({ providerEventId: `graph:${id}`, eventType: `${changeType}:${resource.split('/')[0] || 'resource'}` });
    }
    return { kind: 'events', events: out };
}
// ── aws_eventbridge ─────────────────────────────────────────────────────
/** Header the customer sets on the EventBridge API destination connection. */
export const EVENTBRIDGE_KEY_HEADER = 'x-gacpro-event-key';
async function verifyEventBridge(r) {
    if (r.method !== 'POST')
        return reject('method');
    if (!timingSafeEqual(r.header(EVENTBRIDGE_KEY_HEADER) ?? '', r.secret))
        return reject('api key');
    const e = parseJson(r.rawBody);
    if (!e || typeof e.id !== 'string' || typeof e['detail-type'] !== 'string')
        return reject('shape');
    if (!withinSkew(e.time, r.now))
        return reject('stale');
    return { kind: 'events', events: [{ providerEventId: e.id, eventType: `${e.source ?? 'aws'}:${e['detail-type']}`, timestamp: e.time }] };
}
// ── gcp_pubsub_oidc ─────────────────────────────────────────────────────
const GOOGLE_JWKS = 'https://www.googleapis.com/oauth2/v3/certs';
const GOOGLE_ISSUERS = new Set(['https://accounts.google.com', 'accounts.google.com']);
let jwksCache = null;
/** Last refetch attempt, successful or not: the refresh floor applies to attempts (C-7 L-4). */
let jwksLastAttempt = -Infinity;
/** An unknown `kid` refetches the JWKS at most this often (Google rotates keys; V104-C-5). */
export const JWKS_REFRESH_MIN_MS = 60_000;
function b64urlToBytes(s) {
    const b = atob(s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '='));
    return Uint8Array.from(b, (c) => c.charCodeAt(0));
}
async function googleKeys(fetchImpl, now, refresh = false) {
    const age = jwksCache ? now - jwksCache.at : Infinity;
    if (jwksCache && age < 3_600_000 && !(refresh && age >= JWKS_REFRESH_MIN_MS))
        return jwksCache.keys;
    // At most one JWKS fetch per floor (cold, expired or unknown-kid refresh), even while
    // Google is failing: a request flood never turns into an outbound fetch flood.
    if (now - jwksLastAttempt < JWKS_REFRESH_MIN_MS) {
        if (jwksCache)
            return jwksCache.keys;
        throw new Error('jwks refresh throttled');
    }
    jwksLastAttempt = now;
    const res = await fetchImpl(GOOGLE_JWKS, { redirect: 'manual' });
    if (!res.ok)
        throw new Error('jwks fetch failed');
    const keys = (await res.json()).keys ?? [];
    jwksCache = { at: now, keys };
    return keys;
}
/** Test seam: clear the per-isolate JWKS cache. */
export function resetGoogleJwksCache() { jwksCache = null; jwksLastAttempt = -Infinity; }
async function verifyPubSub(r) {
    if (r.method !== 'POST')
        return reject('method');
    const auth = r.header('authorization') ?? '';
    if (!auth.startsWith('Bearer '))
        return reject('no bearer');
    const parts = auth.slice(7).split('.');
    // Canonical base64url only: no padding, +, / or whitespace variants of the same token.
    if (parts.length !== 3 || !parts.every((p) => /^[A-Za-z0-9_-]+$/.test(p)))
        return reject('jwt shape');
    let header;
    let claims;
    try {
        header = JSON.parse(new TextDecoder().decode(b64urlToBytes(parts[0])));
        claims = JSON.parse(new TextDecoder().decode(b64urlToBytes(parts[1])));
    }
    catch {
        return reject('jwt decode');
    }
    if (header.alg !== 'RS256' || !header.kid)
        return reject('alg');
    const fetchImpl = r.fetchImpl ?? fetch;
    let keys = await googleKeys(fetchImpl, r.now.getTime()).catch(() => null);
    // V104-C-5: a kid we don't hold may be a freshly rotated Google key; refetch (rate-limited) once.
    if (keys && !keys.some((k) => k.kid === header.kid))
        keys = await googleKeys(fetchImpl, r.now.getTime(), true).catch(() => null);
    const jwk = keys?.find((k) => k.kid === header.kid);
    if (!jwk)
        return reject('kid');
    // JsonWebKey / CryptoKey are DOM types; core has no DOM lib, so the jwk overload is typed locally.
    const key = await crypto.subtle.importKey('jwk', jwk, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
    const ok = await crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, b64urlToBytes(parts[2]), enc.encode(`${parts[0]}.${parts[1]}`));
    if (!ok)
        return reject('signature');
    const nowS = Math.floor(r.now.getTime() / 1000);
    const expectedEmail = typeof r.config.pubsub_service_account === 'string' ? r.config.pubsub_service_account : '';
    if (!claims.iss || !GOOGLE_ISSUERS.has(claims.iss))
        return reject('iss');
    if (claims.aud !== r.url.split('?')[0])
        return reject('aud');
    if (typeof claims.exp !== 'number' || claims.exp < nowS)
        return reject('exp');
    if (typeof claims.iat === 'number' && claims.iat > nowS + 60)
        return reject('iat');
    if (!expectedEmail || claims.email !== expectedEmail || claims.email_verified !== true)
        return reject('email');
    const body = parseJson(r.rawBody);
    const m = body?.message;
    if (!m || typeof m.messageId !== 'string' || !/^[0-9A-Za-z_-]{1,128}$/.test(m.messageId))
        return reject('shape');
    if (!withinSkew(m.publishTime, r.now))
        return reject('stale');
    // V104-C-1 / C-7 M-1: dedupe on messageId. Google reuses one push token across many
    // messages for up to an hour, so a token-hash key dropped real events. The JWT does not
    // cover the body, so a captured (still-valid) token can mint new message ids; that buys
    // at most a debounced, rate-limited re-pull, and an event payload is never evidence.
    return { kind: 'events', events: [{ providerEventId: `pubsub:${m.messageId}`, eventType: m.attributes?.eventType ?? 'pubsub.message', timestamp: m.publishTime }] };
}
/** Run the verifier a connector's manifest declares. Unknown/unimplemented → reject. */
export async function verifyEvent(verifier, r) {
    try {
        switch (verifier) {
            case 'github_hmac': return await verifyGithub(r);
            case 'okta_event_hook': return await verifyOkta(r);
            case 'graph_change_notification': return await verifyGraph(r);
            case 'aws_eventbridge': return await verifyEventBridge(r);
            case 'gcp_pubsub_oidc': return await verifyPubSub(r);
            default: return reject('verifier not implemented');
        }
    }
    catch {
        return reject('verifier error');
    }
}
//# sourceMappingURL=event-verifiers.js.map